import React, { useState } from 'react';
import { 
  Box, 
  IconButton, 
  Typography, 
  Avatar, 
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  AppBar,
  Toolbar,
  Drawer
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CodeIcon from '@mui/icons-material/Code';
import HistoryIcon from '@mui/icons-material/History';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import ChatIcon from '@mui/icons-material/Chat';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../context/AuthContext';
import { useFolderContext } from '../context/FolderContext';
import FolderPanel from './folders/FolderPanel';

const MINI_SIDEBAR_WIDTH = 60;

// Mini sidebar for icons when collapsed - hide when expanded
const MiniSidebar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  left: 0,
  top: 0,
  width: MINI_SIDEBAR_WIDTH,
  height: '100vh',
  background: `linear-gradient(180deg, ${theme.palette.background.paper}, ${alpha(theme.palette.background.paper, 0.98)})`,
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  display: 'flex', // Always show
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2, 1),
  zIndex: 1200,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

// Logo in mini sidebar - no rounded corners
const MiniLogo = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 4, // Less rounded, more professional
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '1.2rem',
  cursor: 'pointer',
  marginBottom: theme.spacing(2),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

// Mini sidebar icon button - less rounded
const MiniIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  width: 44,
  height: 44,
  marginBottom: theme.spacing(1),
  borderRadius: 4, // Less rounded
  background: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  }
}));

function ImprovedSidebar({ children, onToggleTheme, darkMode }) {
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [showFolderTree, setShowFolderTree] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  // Mobile detection
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 768px and below
  
  const auth = useAuth() || { user: null, isAuthenticated: false, logout: () => {} };
  const { user, isAuthenticated, logout } = auth;
  const { selectedFolderId, selectFolder } = useFolderContext();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/login');
  };

  // Folder tree handlers
  const handleFolderButtonClick = (e) => {
    e.stopPropagation(); // Prevent sidebar expansion
    setShowFolderTree(!showFolderTree);
  };

  const handleCloseFolderPanel = () => {
    setShowFolderTree(false);
  };

  // Mobile drawer handlers
  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleFolderSelect = (folderId) => {
    selectFolder(folderId);
  };

  // Create sidebar content component for reuse
  const SidebarContent = () => (
    <>
      <MiniLogo onClick={(e) => {
        e.stopPropagation();
        navigate(isAuthenticated ? '/' : '/login');
        if (isMobile) handleMobileDrawerClose();
      }}>
        P
      </MiniLogo>
      
      {isAuthenticated && (
        <>
          <Tooltip title="Dashboard" placement="right">
            <MiniIconButton 
              active={isActive('/')}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/');
                if (isMobile) handleMobileDrawerClose();
              }}
            >
              <DashboardIcon />
            </MiniIconButton>
          </Tooltip>

          <Tooltip title="Snip" placement="right">
            <MiniIconButton 
              active={isActive('/create')}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/create');
                if (isMobile) handleMobileDrawerClose();
              }}
            >
              <CodeIcon />
            </MiniIconButton>
          </Tooltip>

          <Tooltip title="Docs" placement="right">
            <MiniIconButton 
              active={isActive('/docs')}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/docs');
                if (isMobile) handleMobileDrawerClose();
              }}
            >
              <EditIcon />
            </MiniIconButton>
          </Tooltip>

          <Tooltip title="My Snippets" placement="right">
            <MiniIconButton 
              active={isActive('/my-snippets')}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/my-snippets');
                if (isMobile) handleMobileDrawerClose();
              }}
            >
              <HistoryIcon />
            </MiniIconButton>
          </Tooltip>

          <Tooltip title="My Documents" placement="right">
            <MiniIconButton 
              active={isActive('/my-documents')}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/my-documents');
                if (isMobile) handleMobileDrawerClose();
              }}
            >
              <ChatIcon />
            </MiniIconButton>
          </Tooltip>

          <Tooltip title="Folders" placement="right">
            <MiniIconButton 
              active={showFolderTree}
              onClick={handleFolderButtonClick}
            >
              {showFolderTree ? <FolderOpenIcon /> : <FolderIcon />}
            </MiniIconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Toggle Theme" placement="right">
            <MiniIconButton onClick={onToggleTheme}>
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </MiniIconButton>
          </Tooltip>

          <Tooltip title="Profile" placement="right">
            <MiniIconButton onClick={handleUserMenuOpen}>
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  fontSize: '0.875rem',
                  bgcolor: theme.palette.primary.main 
                }}
              >
                {user?.name ? user.name.charAt(0).toUpperCase() : <PersonIcon />}
              </Avatar>
            </MiniIconButton>
          </Tooltip>
        </>
      )}
    </>
  );

  if (isMobile) {
    // Mobile Layout: Header + Drawer + Full Width Content
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Mobile Header */}
        <AppBar 
          position="sticky" 
          elevation={1}
          sx={{ 
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: 56 }}>
            <IconButton
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1,
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
            >
              Pastely
            </Typography>

            {isAuthenticated && (
              <>
                <IconButton onClick={onToggleTheme} sx={{ mr: 1 }}>
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
                
                <IconButton onClick={handleUserMenuOpen}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      fontSize: '0.875rem',
                      bgcolor: theme.palette.primary.main 
                    }}
                  >
                    {user?.name ? user.name.charAt(0).toUpperCase() : <PersonIcon />}
                  </Avatar>
                </IconButton>
              </>
            )}
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileDrawerOpen}
          onClose={handleMobileDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: MINI_SIDEBAR_WIDTH,
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: theme.spacing(2, 1),
              height: '100%'
            }}
          >
            <SidebarContent />
          </Box>
        </Drawer>

        {/* Mobile Main Content - Full Width */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            minHeight: 0,
            overflow: 'auto'
          }}
        >
          {children}
        </Box>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Sign Out
          </MenuItem>
        </Menu>

        {/* Folder Panel */}
        <FolderPanel
          open={showFolderTree}
          onClose={handleCloseFolderPanel}
          onFolderSelect={handleFolderSelect}
          selectedFolderId={selectedFolderId}
        />
      </Box>
    );
  }

  // Desktop Layout: Original Sidebar Layout

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mini Sidebar - Always visible */}
      <MiniSidebar>
        <MiniLogo onClick={(e) => {
          e.stopPropagation();
          navigate(isAuthenticated ? '/' : '/login');
        }}>
          P
        </MiniLogo>
        
        {isAuthenticated && (
          <>
            <Tooltip title="Dashboard" placement="right">
              <MiniIconButton 
                active={isActive('/')}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/');
                }}
              >
                <DashboardIcon />
              </MiniIconButton>
            </Tooltip>

            <Tooltip title="Doc" placement="right">
              <MiniIconButton
                active={isActive('/docs')}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/docs');
                }}
              >
                <EditIcon />
              </MiniIconButton>
            </Tooltip>

            <Tooltip title="Snip" placement="right">
              <MiniIconButton 
                active={isActive('/create')}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/create');
                }}
              >
                <CodeIcon />
              </MiniIconButton>
            </Tooltip>

             <Tooltip title="History" placement="right">
              <MiniIconButton
                active={isActive('/snippets')}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/snippets');
                }}
              >
                <HistoryIcon />
              </MiniIconButton>
            </Tooltip>


          <Tooltip title="Folders" placement="right">
              <MiniIconButton
                active={showFolderTree}
                onClick={handleFolderButtonClick}
              >
                <FolderIcon />
              </MiniIconButton>
            </Tooltip>
          </>
        )}
        
        <Box sx={{ flex: 1 }} />
        
        <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"} placement="right">
          <MiniIconButton onClick={(e) => {
            e.stopPropagation();
            onToggleTheme();
          }}>
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </MiniIconButton>
        </Tooltip>
        
        {isAuthenticated && (
          <Tooltip title="Profile" placement="right">
            <Avatar
              src={user?.picture}
              onClick={(e) => {
                e.stopPropagation();
                handleUserMenuOpen(e);
              }}
              sx={{
                width: 36,
                height: 36,
                cursor: 'pointer',
                background: !user?.picture ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` : undefined,
              }}
            >
              {!user?.picture && (user?.firstName?.[0] || 'U')}
            </Avatar>
          </Tooltip>
        )}
      </MiniSidebar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings'); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>

      {/* Main Content Area - Let theme control height */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Removed minHeight: '100vh' - let theme handle it
          background: theme.palette.background.default,
          // Calculate left margin based on folder panel state
          marginLeft: showFolderTree ? `${MINI_SIDEBAR_WIDTH + 280}px` : `${MINI_SIDEBAR_WIDTH}px`,
          // Remove ALL other margins and padding
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          paddingTop: 0,
          paddingRight: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          // Force zero spacing on children
          '& > *': {
            mt: 0, // Remove margin from first child
          },
          '& > *:first-child': {
            mt: '0 !important',
            pt: '0 !important',
          },
          transition: theme.transitions.create(['margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        {/* Render children normally */}
        {children}
      </Box>

      {/* Folder Panel - Slides out from sidebar */}
      <FolderPanel
        open={showFolderTree}
        onClose={handleCloseFolderPanel}
        onFolderSelect={handleFolderSelect}
        selectedFolderId={selectedFolderId}
      />
    </Box>
  );
}

export default ImprovedSidebar;
